import React, { useState, useEffect } from 'react';
import { X, Copy, Check, FileText, Eye } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import ReactMarkdown from 'react-markdown';
import { Spinner } from '../../design-system';

interface ViewPromptsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
}

interface PromptData {
  workspace_id: string;
  agent_type: string;
  mode?: string;
  template_info?: any;
  integrations: string[];
  prompt_length: number;
  prompt: string;
}

const AGENT_TYPES = [
  { value: 'coder', label: 'Coder Agent', modes: ['vibe', 'code'] },
  { value: 'investigation', label: 'Investigation Agent' },
  { value: 'browser', label: 'Browser Agent' },
];

const ViewPromptsModal: React.FC<ViewPromptsModalProps> = ({ isOpen, onClose, workspaceId }) => {
  const authenticatedApi = useAuthenticatedApi();
  const [selectedAgent, setSelectedAgent] = useState('coder');
  const [selectedMode, setSelectedMode] = useState('code');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promptData, setPromptData] = useState<PromptData | null>(null);
  const [viewMode, setViewMode] = useState<'raw' | 'rendered'>('raw');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && workspaceId && selectedAgent) {
      const mode = selectedAgent === 'coder' ? selectedMode : undefined;
      fetchPrompt(selectedAgent, mode);
    }
  }, [isOpen, workspaceId, selectedAgent, selectedMode]);

  const fetchPrompt = async (type: string, mode?: string) => {
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        workspace_id: workspaceId,
        agent_type: type,
        ...(mode && { mode })
      };
      
      const response = await authenticatedApi.post(apiEndpoints.admin.debugWorkspacePrompt, payload);
      setPromptData(response);
    } catch (err: any) {
      console.error('Error fetching prompt:', err);
      setError(err.message || 'Failed to fetch prompt');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!promptData) return;
    
    try {
      await navigator.clipboard.writeText(promptData.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const selectedAgentConfig = AGENT_TYPES.find(a => a.value === selectedAgent);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-ui-background-secondary rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border-primary">
          <h2 className="text-xl font-semibold text-ui-text-primary">
            View Agent Prompts
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-ui-background-tertiary transition-colors"
          >
            <X size={20} className="text-ui-text-tertiary" />
          </button>
        </div>

        {/* Controls */}
        <div className="px-6 py-4 border-b border-ui-border-primary">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Agent Type Selector */}
            <div>
              <label className="block text-sm font-medium text-ui-text-secondary mb-1">
                Agent Type
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="px-3 py-2 border border-ui-border-primary rounded-md bg-ui-background-secondary text-ui-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {AGENT_TYPES.map(agent => (
                  <option key={agent.value} value={agent.value}>
                    {agent.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Mode Selector (only for coder agent) */}
            {selectedAgent === 'coder' && selectedAgentConfig?.modes && (
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-1">
                  Mode
                </label>
                <select
                  value={selectedMode}
                  onChange={(e) => setSelectedMode(e.target.value)}
                  className="px-3 py-2 border border-ui-border-primary rounded-md bg-ui-background-secondary text-ui-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {selectedAgentConfig.modes.map(mode => (
                    <option key={mode} value={mode}>
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* View Mode Toggle */}
            <div className="flex items-center bg-ui-background-tertiary rounded-md p-1">
              <button
                onClick={() => setViewMode('rendered')}
                className={`flex items-center px-3 py-1.5 rounded transition-colors ${
                  viewMode === 'rendered'
                    ? 'bg-ui-background-secondary text-ui-text-primary shadow-sm'
                    : 'text-ui-text-secondary hover:text-ui-text-primary'
                }`}
              >
                <Eye size={14} className="mr-1.5" />
                Rendered
              </button>
              <button
                onClick={() => setViewMode('raw')}
                className={`flex items-center px-3 py-1.5 rounded transition-colors ${
                  viewMode === 'raw'
                    ? 'bg-ui-background-secondary text-ui-text-primary shadow-sm'
                    : 'text-ui-text-secondary hover:text-ui-text-primary'
                }`}
              >
                <FileText size={14} className="mr-1.5" />
                Raw
              </button>
            </div>

            {/* Copy Button */}
            <button
              onClick={copyToClipboard}
              disabled={!promptData || loading}
              className="ml-auto flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {copied ? (
                <>
                  <Check size={14} className="mr-1.5" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={14} className="mr-1.5" />
                  Copy
                </>
              )}
            </button>
          </div>

          {/* Metadata */}
          {promptData && (
            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              <div>
                <span className="text-ui-text-secondary">Length:</span>
                <span className="ml-1 text-ui-text-primary font-medium">
                  {promptData.prompt_length.toLocaleString()} characters
                </span>
              </div>
              {promptData.integrations.length > 0 && (
                <div>
                  <span className="text-ui-text-secondary">Integrations:</span>
                  <span className="ml-1 text-ui-text-primary font-medium">
                    {promptData.integrations.join(', ')}
                  </span>
                </div>
              )}
              {promptData.template_info && (
                <div>
                  <span className="text-ui-text-secondary">Template:</span>
                  <span className="ml-1 text-ui-text-primary font-medium">
                    {promptData.template_info.name}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <Spinner size={32} />
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {!loading && !error && promptData && (
            <div className="prose dark:prose-invert max-w-none">
              {viewMode === 'rendered' ? (
                <ReactMarkdown
                  className="text-sm prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-h4:text-base prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-code:text-sm"
                  components={{
                    pre: ({ children, ...props }) => (
                      <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-md overflow-x-auto text-sm" {...props}>
                        {children}
                      </pre>
                    ),
                    code: ({ node, inline, className, children, ...props }: any) => {
                      return inline ? (
                        <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono" {...props}>
                          {children}
                        </code>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    },
                    h1: ({ children, ...props }) => (
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 mt-6" {...props}>
                        {children}
                      </h1>
                    ),
                    h2: ({ children, ...props }) => (
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-5" {...props}>
                        {children}
                      </h2>
                    ),
                    h3: ({ children, ...props }) => (
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 mt-4" {...props}>
                        {children}
                      </h3>
                    ),
                    h4: ({ children, ...props }) => (
                      <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2 mt-3" {...props}>
                        {children}
                      </h4>
                    ),
                    p: ({ children, ...props }) => (
                      <p className="text-gray-700 dark:text-gray-300 mb-3 leading-relaxed" {...props}>
                        {children}
                      </p>
                    ),
                    ul: ({ children, ...props }) => (
                      <ul className="list-disc list-inside mb-3 space-y-1" {...props}>
                        {children}
                      </ul>
                    ),
                    ol: ({ children, ...props }) => (
                      <ol className="list-decimal list-inside mb-3 space-y-1" {...props}>
                        {children}
                      </ol>
                    ),
                    li: ({ children, ...props }) => (
                      <li className="text-gray-700 dark:text-gray-300" {...props}>
                        {children}
                      </li>
                    ),
                    blockquote: ({ children, ...props }) => (
                      <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-600 dark:text-gray-400 my-4" {...props}>
                        {children}
                      </blockquote>
                    ),
                    a: ({ children, href, ...props }) => (
                      <a href={href} className="text-blue-600 dark:text-blue-400 underline hover:no-underline" {...props}>
                        {children}
                      </a>
                    ),
                  }}
                >
                  {promptData.prompt}
                </ReactMarkdown>
              ) : (
                <pre className="bg-ui-background-tertiary p-4 rounded-md overflow-x-auto text-sm font-mono">
                  {promptData.prompt}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewPromptsModal; 